import { type FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckMarkIcon from '@assets/img/icons/check.svg?react';
import ChevronIcon from '@assets/img/icons/chevron.svg?react';
import { type NextBuild, type UnionGameItemType } from '@common/types/userType';
import { HandPointer } from '@components/Intro/HandPointer/HandPointer.tsx';
import { IntroContext } from '@components/Intro/Intro.tsx';
import { Container } from '@components/ui/Container/container';
import { type RootState, useAppSelector } from '@redux/store';
import cn from 'classnames';

import { useCheckRequirements } from '@/common/hooks/check-requirements';
import { getImage } from '@/common/lib/images';
import { getText } from '@/common/lib/text';
import { createRequirementLink } from '@/components/RequirementsItem/requirement-link';
import { Picture } from '@/components/ui/Picture/Picture';
import { selectNextToBuildByBuidlKind } from '@/redux/selectors/common';
import { selectBuildLevelByName } from '@/redux/selectors/userSelectors';

import hummerIcon from '/img/contract-lock-img/Hummer.png';

const NextIntroBuildItem: FC<NextBuild> = ({ id, plant, level }) => {
  const { t } = useTranslation();
  const { startOnboarding } = useContext(IntroContext);
  const userPlants = useAppSelector((state) => state.user.user?.plants);
  const nextToBuild = useSelector((state: RootState) =>
    selectNextToBuildByBuidlKind(state, plant),
  );

  const [check, setCheck] = useState(false);
  const [isPlantExist, setPlantExist] = useState(false);

  const nextToBuildRequirements = nextToBuild
    ? nextToBuild.creationRequirements
    : undefined;

  const nextToBuildCheck = useCheckRequirements(nextToBuildRequirements);

  useEffect(() => {
    if (nextToBuild?.name === plant) {
      setCheck(nextToBuildCheck.isComplete);
    }
  }, [nextToBuild, plant, nextToBuildCheck]);

  useEffect(() => {
    if (userPlants && Object.keys(userPlants).includes(plant)) {
      setPlantExist(true);
    }
  }, [userPlants, plant]);

  const navigate = useNavigate();
  const plantLevel = useAppSelector((state) =>
    selectBuildLevelByName(state, plant as UnionGameItemType),
  );
  const [plantName, setPlantName] = useState('');

  useEffect(() => {
    const name = textItem();
    setPlantName(name ? name : '');
  }, [plant]);

  const image = getImage({
    name: plant,
    field: 'build',
  });

  const navigateItems = (): void => {
    const link = createRequirementLink({
      value: level,
      type: 'plant',
      name: plant,
      plantLevel: plantLevel || 0,
    });

    if (link) {
      navigate(link, { state: { plant } });
    }
  };

  function textItem(): string | undefined {
    return getText({
      name: plant,
      kind: 'build',
      t,
    });
  }

  const doTask = (): void | (() => void) => {
    if (isPlantExist || !check) return;
    navigateItems();
    startOnboarding(plant);
  };

  //animation hummer
  useEffect(() => {
    const animateDiv = document.getElementById('animate-hummer');

    if (!animateDiv || !check) return;

    const animate = () => {
      animateDiv.style.transition = 'transform 0.7s';
      animateDiv.style.transform = 'rotate(90deg)';

      const time = setTimeout(() => {
        animateDiv.style.transform = 'rotate(0)';
      }, 700);

      return (): void => clearTimeout(time);
    };

    const firstAnimation = setTimeout(() => {
      animate();
    }, 500);

    const animationInterval = setInterval(animate, 3000);

    return (): void => {
      clearTimeout(firstAnimation);
      clearInterval(animationInterval);
    };
  }, [check]);
  //

  return (
    <div className="relative">
      <Container
        key={id}
        onClick={doTask}
        className={cn(
          'w-full flex items-center justify-between mb-[0.25rem] px-2 pt-1',
          check ? '' : 'cursor-pointer',
          'taskItemBackground',
        )}
        variant="task"
      >
        <div className={cn('absolute', check && 'colorSegmentIntro')}></div>
        <div className="flex items-center gap-2">
          {image && (
            <Picture
              className="w-16 h-16"
              img={image.img}
              // webp={image.webp}
              alt={plant}
            />
          )}
          <div className="nextBuild_info">
            <span>{t('Build')}</span>
            <p>{plantName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {check && (
            <div id="animate-hummer">
              <img src={hummerIcon} alt="" className="w-4 h-4" />
            </div>
          )}
          {isPlantExist ? (
            <CheckMarkIcon className="w-4 h-4 mr-2 text-accent" />
          ) : (
            <ChevronIcon className="w-6 h-6 text-white/40" />
          )}
        </div>
      </Container>
      {check && (
        <HandPointer className="absolute top-8 left-[60%] w-[72px] h-[72px] z-10" />
      )}
    </div>
  );
};

export { NextIntroBuildItem };
